import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { distributionTotals } from '../../domain/distributionCalc';
import { date, money } from '../../lib/format';
import { HOLDER_TYPE_LABELS } from '../../lib/labels';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';

export type ReportKey =
  | 'concentration'
  | 'holderTypes'
  | 'distributionHistory'
  | 'arrears'
  | 'engagement'
  | 'dataQuality';

export const REPORTS: { key: ReportKey; name: string; description: string }[] = [
  {
    key: 'concentration',
    name: 'Top holders and concentration',
    description: 'Largest holders by fund, with % of class',
  },
  {
    key: 'holderTypes',
    name: 'Holder mix',
    description: 'Value by entity type (SMSF, trust, individual…)',
  },
  {
    key: 'distributionHistory',
    name: 'Distribution history',
    description: 'Every distribution with gross, withholding and DRP',
  },
  {
    key: 'arrears',
    name: 'Capital call arrears',
    description: 'Unpaid and partial call amounts by holder',
  },
  {
    key: 'engagement',
    name: 'Investor engagement',
    description: 'App logins, MFA adoption, statement delivery',
  },
  {
    key: 'dataQuality',
    name: 'Data quality',
    description: 'Missing TFNs, unverified banks, expiring certificates',
  },
];

const COLORS = ['#4F46E5', '#0F766E', '#B45309', '#9333EA', '#0369A1', '#BE123C'];

export default function ReportPreview({ reportKey }: { reportKey: ReportKey }) {
  const scope = useTenantScope();
  const holderValue = useMemo(() => {
    const map = new Map<string, number>();
    for (const holding of scope.holdings)
      map.set(holding.holderId, (map.get(holding.holderId) ?? 0) + holding.value);
    return map;
  }, [scope.holdings]);

  if (reportKey === 'concentration') {
    const total = sumBy(scope.holdings, (holding) => holding.value);
    const rows = Array.from(holderValue.entries())
      .map(([holderId, value]) => ({ holderId, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
    return (
      <DataTable
        rows={rows}
        rowKey={(row) => row.holderId}
        dense
        columns={[
          { key: 'rank', label: '#', render: (row) => rows.indexOf(row) + 1 },
          {
            key: 'holder',
            label: 'Holder',
            render: (row) => scope.holderById.get(row.holderId)?.name,
          },
          { key: 'value', label: 'Value', align: 'right', render: (row) => money(row.value) },
          {
            key: 'pct',
            label: '% of register',
            align: 'right',
            render: (row) => `${((row.value / total) * 100).toFixed(2)}%`,
          },
        ]}
      />
    );
  }
  if (reportKey === 'holderTypes') {
    const byType = new Map<string, number>();
    for (const [holderId, value] of holderValue) {
      const type = scope.holderById.get(holderId)?.type ?? 'INDIVIDUAL';
      byType.set(type, (byType.get(type) ?? 0) + value);
    }
    const data = Array.from(byType.entries()).map(([type, value]) => ({
      name: HOLDER_TYPE_LABELS[type as keyof typeof HOLDER_TYPE_LABELS],
      value: Math.round(value),
    }));
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Box sx={{ height: 260 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                isAnimationActive={false}
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
              >
                {data.map((entry, index) => (
                  <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => money(value)} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box>
          {data
            .sort((a, b) => b.value - a.value)
            .map((row) => (
              <Typography
                key={row.name}
                variant="body2"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                  borderBottom: '1px solid #E2E8F0',
                }}
              >
                <span>{row.name}</span>
                <strong>{money(row.value)}</strong>
              </Typography>
            ))}
        </Box>
      </Box>
    );
  }
  if (reportKey === 'distributionHistory') {
    return (
      <DataTable
        rows={scope.distributions}
        rowKey={(row) => row.id}
        dense
        defaultSort={{ key: 'date', direction: 'desc' }}
        columns={[
          {
            key: 'fund',
            label: 'Fund',
            render: (row) =>
              `${scope.fundById.get(row.fundId)?.code} ${scope.classById.get(row.classId)?.code}`,
          },
          {
            key: 'date',
            label: 'Paid',
            sortValue: (row) => row.paymentDate,
            render: (row) => date(row.paymentDate),
          },
          {
            key: 'gross',
            label: 'Gross',
            align: 'right',
            render: (row) => money(distributionTotals(row).gross),
          },
          {
            key: 'wht',
            label: 'Withheld',
            align: 'right',
            render: (row) =>
              money(distributionTotals(row).tfnWithholding + distributionTotals(row).nrWithholding),
          },
          {
            key: 'drp',
            label: 'DRP',
            align: 'right',
            render: (row) => money(distributionTotals(row).drp),
          },
          {
            key: 'status',
            label: 'Status',
            render: (row) => row.status.toLowerCase().replace('_', ' '),
          },
        ]}
      />
    );
  }
  if (reportKey === 'arrears') {
    const rows = scope.calls
      .filter((call) => call.status === 'ISSUED')
      .flatMap((call) =>
        call.lines.filter((line) => line.status !== 'PAID').map((line) => ({ call, line })),
      );
    return (
      <DataTable
        rows={rows}
        rowKey={(row) => `${row.call.id}-${row.line.holderId}`}
        dense
        emptyMessage="No arrears"
        columns={[
          {
            key: 'holder',
            label: 'Holder',
            render: (row) => scope.holderById.get(row.line.holderId)?.name,
          },
          {
            key: 'call',
            label: 'Call',
            render: (row) => `${scope.fundById.get(row.call.fundId)?.code} Call ${row.call.number}`,
          },
          { key: 'due', label: 'Due', render: (row) => date(row.call.dueDate) },
          {
            key: 'out',
            label: 'Outstanding',
            align: 'right',
            render: (row) => money(row.line.amount - row.line.paid),
          },
        ]}
      />
    );
  }
  const holders = scope.holders.filter((holder) => holderValue.has(holder.id));
  const rows =
    reportKey === 'engagement'
      ? [
          {
            metric: 'Holders with an investor app login',
            value: `${holders.filter((holder) => holder.investorLoginId).length} of ${holders.length}`,
          },
          {
            metric: 'Statements delivered electronically',
            value: `${Math.round((holders.filter((holder) => holder.communication === 'EMAIL').length / Math.max(1, holders.length)) * 100)}%`,
          },
          { metric: 'Tasks completed in app (30 days)', value: '18' },
          { metric: 'Average time to complete an application', value: '2.4 days' },
          { metric: 'Distribution advice open rate', value: '71%' },
        ]
      : [
          {
            metric: 'TFN / ABN not quoted',
            value: String(holders.filter((holder) => holder.tfnStatus === 'NOT_QUOTED').length),
          },
          {
            metric: 'Bank account not verified',
            value: String(holders.filter((holder) => !holder.bank.verified).length),
          },
          {
            metric: 'Wholesale certificate expiring within 90 days',
            value: String(
              holders.filter(
                (holder) =>
                  holder.wholesale?.expiresAt && holder.wholesale.expiresAt <= '2027-01-14',
              ).length,
            ),
          },
          {
            metric: 'KYC refresh overdue',
            value: String(holders.filter((holder) => holder.kycStatus === 'EXPIRED').length),
          },
          {
            metric: 'Beneficial owners unverified',
            value: String(
              holders
                .flatMap((holder) => holder.beneficialOwners)
                .filter((owner) => owner.kycStatus !== 'VERIFIED').length,
            ),
          },
        ];
  return (
    <DataTable
      rows={rows}
      rowKey={(row) => row.metric}
      dense
      columns={[
        { key: 'metric', label: 'Measure', render: (row) => row.metric },
        {
          key: 'value',
          label: 'Value',
          align: 'right',
          render: (row) => <strong>{row.value}</strong>,
        },
      ]}
    />
  );
}
